import React, { useState, useEffect, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import axios from "axios";
import "./AttendanceCalendar.css";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  IconButton,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Avatar,
  Tooltip,
  Card,
  Divider,
  Badge,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Stack,
  useTheme,
} from "@mui/material";
import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getToken, base_url } from "../../config/config";
import {
  format,
  parseISO,
  isValid,
  startOfDay,
  endOfDay,
  differenceInHours,
} from "date-fns";
import { de } from "date-fns/locale";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";

const AttendanceCalendar = () => {
  const theme = useTheme();
  const [events, setEvents] = useState([]);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    type: "info",
    message: "",
  });
  const [employees, setEmployees] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [calendarView, setCalendarView] = useState("dayGridMonth");
  const [stats, setStats] = useState({
    totalAttendance: 0,
    totalAbsences: 0,
    totalHours: 0,
    employeeStats: [],
  });
  const [filterOptions, setFilterOptions] = useState({
    employee: "",
    dateRange: { start: null, end: null },
    showAbsent: true,
  });
  const [holidays, setHolidays] = useState([]);

  const [formData, setFormData] = useState({
    user: "",
    Date: format(new Date(), "yyyy-MM-dd"),
    StartTime: "",
    endTime: "",
    Abwesend: false,
    AbwesendType: "",
  });

  useEffect(() => {
    fetchEmployees();
    fetchAllAttendance();
    fetchHolidays();
  }, []);

  // Fetch holidays when month or employee filter changes
  useEffect(() => {
    fetchHolidays();
  }, [selectedMonth, filterOptions.employee]);

  useEffect(() => {
    if (events.length > 0) {
      calculateStats();
    }
  }, [events, filterOptions]);

  const calculateStats = () => {
    let filteredEvents = [...events];

    // Apply employee filter if selected
    if (filterOptions.employee) {
      filteredEvents = filteredEvents.filter(
        (event) => event.title.split(" - ")[0] === filterOptions.employee
      );
    }

    // Apply date range filter if selected
    if (filterOptions.dateRange.start && filterOptions.dateRange.end) {
      filteredEvents = filteredEvents.filter((event) => {
        const eventDate = new Date(event.start);
        return (
          eventDate >= filterOptions.dateRange.start &&
          eventDate <= filterOptions.dateRange.end
        );
      });
    }

    // Calculate stats
    const totalAttendance = filteredEvents.filter(
      (event) => !event.extendedProps.abwesend
    ).length;
    const totalAbsences = filteredEvents.filter(
      (event) => event.extendedProps.abwesend
    ).length;

    // Calculate total hours
    let totalHours = 0;
    filteredEvents.forEach((event) => {
      if (
        !event.extendedProps.abwesend &&
        event.extendedProps.startTime &&
        event.extendedProps.endTime
      ) {
        // Format could be "HH:mm" or "HH:mm:ss"
        const start = event.extendedProps.startTime.split(":");
        const end = event.extendedProps.endTime.split(":");

        if (start.length >= 2 && end.length >= 2) {
          const startHour = parseInt(start[0]);
          const startMin = parseInt(start[1]);
          const endHour = parseInt(end[0]);
          const endMin = parseInt(end[1]);

          // Calculate hours as a decimal
          const hours = endHour - startHour + (endMin - startMin) / 60;
          if (hours > 0) {
            totalHours += hours;
          }
        }
      }
    });

    // Calculate per-employee stats
    const employeeStats = employees.map((employee) => {
      const employeeEvents = filteredEvents.filter(
        (event) => event.title.split(" - ")[0] === employee.name
      );

      const attendanceDays = employeeEvents.filter(
        (event) => !event.extendedProps.abwesend
      ).length;
      const absenceDays = employeeEvents.filter(
        (event) => event.extendedProps.abwesend
      ).length;

      let employeeHours = 0;
      employeeEvents.forEach((event) => {
        if (
          !event.extendedProps.abwesend &&
          event.extendedProps.startTime &&
          event.extendedProps.endTime
        ) {
          const start = event.extendedProps.startTime.split(":");
          const end = event.extendedProps.endTime.split(":");

          if (start.length >= 2 && end.length >= 2) {
            const startHour = parseInt(start[0]);
            const startMin = parseInt(start[1]);
            const endHour = parseInt(end[0]);
            const endMin = parseInt(end[1]);

            const hours = endHour - startHour + (endMin - startMin) / 60;
            if (hours > 0) {
              employeeHours += hours;
            }
          }
        }
      });

      return {
        name: employee.name,
        attendanceDays,
        absenceDays,
        totalHours: employeeHours.toFixed(2),
      };
    });

    setStats({
      totalAttendance,
      totalAbsences,
      totalHours: totalHours.toFixed(2),
      employeeStats,
    });
  };

  const fetchEmployees = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${base_url}/v1/getAllEmployees/`);
      console.log("Employee response:", response.data);

      // Check if response.data is an array directly (without the .data property)
      const employeeData = Array.isArray(response.data)
        ? response.data
        : response.data && Array.isArray(response.data.data)
        ? response.data.data
        : [];

      if (employeeData.length > 0) {
        const formattedEmployees = employeeData.map((employee) => ({
          id: employee._id,
          name:
            employee.name ||
            `${employee.firstName || ""} ${employee.lastName || ""}`.trim(),
          email: employee.email || "",
          role: employee.role || "",
          avatar: employee.avatar || "",
        }));
        console.log("Formatted employees:", formattedEmployees);
        setEmployees(formattedEmployees);
      } else {
        console.error("No employee data found");
        setAlert({
          show: true,
          type: "warning",
          message: "No employees found. Please add employees first.",
        });
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      setAlert({
        show: true,
        type: "error",
        message: "Failed to fetch employees. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllAttendance = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${base_url}/v1/getDailyAttendance`);
      if (response.data && response.data.data) {
        const attendanceData = response.data.data.map((record) => {
          const isAbwesend = record.Abwesend || false;
          const date = record.Date
            ? record.Date
            : format(new Date(record.createdAt), "yyyy-MM-dd");

          return {
            title: `${record.user} - ${record.StartTime || "N/A"}`,
            start: date,
            end: date,
            id: record._id,
            backgroundColor: isAbwesend ? "#ED293A" : "#78B660",
            borderColor: isAbwesend ? "#ED293A" : "#78B660",
            textColor: "white",
            extendedProps: {
              abwesend: isAbwesend,
              abwesendType: record.AbwesendType || "",
              startTime: record.StartTime || "",
              endTime: record.endTime || "",
              stundenType: record.StundenType || "",
              stunden: record.Stunden || "",
            },
          };
        });
        setEvents(attendanceData);
      }
    } catch (error) {
      console.error("Error fetching attendance records:", error);
      setAlert({
        show: true,
        type: "error",
        message: "Failed to fetch attendance records. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHolidays = async () => {
    try {
      // Format month correctly: use the first day of the month to ensure correct month
      console.log("=== fetchHolidays called ===");
      console.log("selectedMonth:", selectedMonth);
      console.log("selectedMonth.toISOString():", selectedMonth.toISOString());
      
      const year = selectedMonth.getFullYear();
      const monthIndex = selectedMonth.getMonth(); // 0 = January, 11 = December

      console.log("Year from getFullYear():", year);
      console.log("MonthIndex from getMonth():", monthIndex, "(0=Jan, 11=Dec)");

      // Manually format month string to ensure it's correct (pad with 0)
      const monthString = String(monthIndex + 1).padStart(2, "0");
      const month = `${year}-${monthString}`;

      console.log("Calculated monthString:", monthString);
      console.log(
        "Final month to send to API:",
        month
      );

      let url = `${base_url}/v1/getHolidayByMonth/${month}`;

      // Add employee name filter if selected
      if (filterOptions.employee) {
        url += `?employeeName=${encodeURIComponent(filterOptions.employee)}`;
      }

      console.log("Fetching holidays from:", url);
      const response = await axios.get(url);
      console.log("Holiday response:", response.data);

      // Response is directly an array, not wrapped in .data property
      const holidaysArray = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      if (holidaysArray && holidaysArray.length > 0) {
        const holidayData = holidaysArray.map((holiday) => {
          // Format the date properly - extract just the date part from ISO string
          const holidayDate = format(
            parseISO(holiday.holidayDate),
            "yyyy-MM-dd"
          );

          return {
            title: `${holiday.employeeName || "Holiday"} - ${
              holiday.employeeHolidayType || "Holiday"
            }`,
            start: holidayDate,
            end: holidayDate,
            id: `holiday-${holiday._id}`,
            backgroundColor: "#FFB81C",
            borderColor: "#FFA500",
            textColor: "#000",
            extendedProps: {
              isHoliday: true,
              employeeName: holiday.employeeName || "",
              holidayType: holiday.employeeHolidayType || "",
              description: holiday.description || "",
            },
          };
        });
        console.log("Processed holidays:", holidayData);
        setHolidays(holidayData);
      } else {
        setHolidays([]);
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
      // Don't show error for holidays as it's optional
      setHolidays([]);
    }
  };

  const handleDateClick = (info) => {
    // Format the date to ensure it's in YYYY-MM-DD format
    const clickedDate = format(new Date(info.dateStr), "yyyy-MM-dd");

    setFormData({
      user: "",
      Date: clickedDate,
      StartTime: "",
      endTime: "",
      Abwesend: false,
      AbwesendType: "",
    });
    setCurrentRecord(null);
    setIsModalOpen(true);
  };

  const handleEventClick = (info) => {
    // Check if it's a holiday event
    if (info.event.extendedProps.isHoliday) {
      const employeeName = info.event.extendedProps.employeeName;
      const holidayType = info.event.extendedProps.holidayType;
      const description = info.event.extendedProps.description;
      const date = format(new Date(info.event.start), "yyyy-MM-dd");

      setAlert({
        show: true,
        type: "info",
        message: `${employeeName} has ${holidayType} on ${date}${
          description ? ` - ${description}` : ""
        }`,
      });
      return;
    }

    const event = events.find((e) => e.id === info.event.id);
    if (event) {
      const userName = event.title.split(" - ")[0];

      setFormData({
        user: userName,
        Date: event.start ? format(new Date(event.start), "yyyy-MM-dd") : "",
        StartTime: event.extendedProps.startTime || "",
        endTime: event.extendedProps.endTime || "",
        Abwesend: event.extendedProps.abwesend || false,
        AbwesendType: event.extendedProps.abwesendType || "",
      });
      setCurrentRecord(event);
      setIsModalOpen(true);
    }
  };

  const handleDatesSet = (arg) => {
    // Update selectedMonth when calendar dates change
    // arg.start may include days from previous month (e.g., Jan 26 for Feb view)
    // Calculate middle of range to get actual displayed month
    console.log("handleDatesSet called");
    console.log("arg.start:", arg.start);
    console.log("arg.end:", arg.end);
    
    // Calculate the middle date of the visible range - this is always in the displayed month
    const startTime = new Date(arg.start).getTime();
    const endTime = new Date(arg.end).getTime();
    const middleTime = startTime + (endTime - startTime) / 2;
    const middleDate = new Date(middleTime);
    
    console.log("Middle of range:", middleDate.toISOString());
    console.log("Year:", middleDate.getFullYear(), "Month (0-indexed):", middleDate.getMonth());
    
    setSelectedMonth(middleDate);
  };

  const handleSaveAttendance = async () => {
    setIsLoading(true);
    try {
      // Validate form data
      if (!formData.user) {
        setAlert({
          show: true,
          type: "error",
          message: "Please select an employee",
        });
        setIsLoading(false);
        return;
      }

      if (!formData.Date) {
        setAlert({
          show: true,
          type: "error",
          message: "Please select a date",
        });
        setIsLoading(false);
        return;
      }

      if (!formData.Abwesend && (!formData.StartTime || !formData.endTime)) {
        setAlert({
          show: true,
          type: "error",
          message: "Please provide both start and end times or mark as absent",
        });
        setIsLoading(false);
        return;
      }

      // Format the date to ensure it's in YYYY-MM-DD format
      const formattedDate = format(new Date(formData.Date), "yyyy-MM-dd");
      const dataToSubmit = {
        ...formData,
        Date: formattedDate,
      };

      if (currentRecord) {
        // Update existing record
        await axios.put(
          `${base_url}/v1/updateAttendancebyID/${currentRecord.id}`,
          dataToSubmit
        );

        setAlert({
          show: true,
          type: "success",
          message: "Attendance record updated successfully",
        });
      } else {
        // Add new record
        const response = await axios.post(
          `${base_url}/v1/createAttendanceDashboard`,
          dataToSubmit
        );

        setAlert({
          show: true,
          type: "success",
          message: "Attendance record added successfully",
        });
      }

      // Refresh data
      await fetchAllAttendance();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error saving attendance:", error);
      setAlert({
        show: true,
        type: "error",
        message: error.response?.data?.message || "Error saving attendance",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAttendance = async () => {
    if (!currentRecord || !currentRecord.id) return;

    setIsLoading(true);
    try {
      await axios.delete(`${base_url}/v1/deleteAttendance/${currentRecord.id}`);

      // Remove from local state
      setEvents(events.filter((event) => event.id !== currentRecord.id));

      setAlert({
        show: true,
        type: "success",
        message: "Attendance record deleted successfully",
      });

      setIsModalOpen(false);
    } catch (error) {
      console.error("Error deleting attendance:", error);
      setAlert({
        show: true,
        type: "error",
        message: "Error deleting attendance record",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleFilterChange = (field, value) => {
    setFilterOptions((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const filteredEvents = useMemo(() => {
    let filtered = [...events];
    let filteredHolidays = [...holidays];

    // Apply employee filter - only to attendance events
    if (filterOptions.employee) {
      filtered = filtered.filter(
        (event) => event.title.split(" - ")[0] === filterOptions.employee
      );
      // Also filter holidays by employee name
      filteredHolidays = filteredHolidays.filter(
        (event) => event.extendedProps.employeeName === filterOptions.employee
      );
    }

    // Apply date range filter
    if (filterOptions.dateRange.start && filterOptions.dateRange.end) {
      filtered = filtered.filter((event) => {
        const eventDate = new Date(event.start);
        return (
          eventDate >= filterOptions.dateRange.start &&
          eventDate <= filterOptions.dateRange.end
        );
      });
      filteredHolidays = filteredHolidays.filter((event) => {
        const eventDate = new Date(event.start);
        return (
          eventDate >= filterOptions.dateRange.start &&
          eventDate <= filterOptions.dateRange.end
        );
      });
    }

    // Apply absence filter - only to attendance events
    if (!filterOptions.showAbsent) {
      filtered = filtered.filter((event) => !event.extendedProps.abwesend);
    }

    // Combine attendance events with holidays
    const combined = [...filtered, ...filteredHolidays];
    console.log(
      "Total events to display:",
      combined.length,
      "Attendance:",
      filtered.length,
      "Holidays:",
      filteredHolidays.length
    );
    return combined;
  }, [events, filterOptions, holidays]);

  const handleClearFilters = () => {
    setFilterOptions({
      employee: "",
      dateRange: { start: null, end: null },
      showAbsent: true,
    });
  };

  // Custom rendering for calendar events
  const renderEventContent = (eventInfo) => {
    const isHoliday = eventInfo.event.extendedProps.isHoliday;
    const isAbsent = eventInfo.event.extendedProps.abwesend;
    const userName = eventInfo.event.title.split(" - ")[0];
    const time = eventInfo.event.title.split(" - ")[1];
    const absentType = eventInfo.event.extendedProps.abwesendType || "";
    const holidayType = eventInfo.event.extendedProps.holidayType || "";

    if (isHoliday) {
      return (
        <Tooltip title={`${userName} - ${holidayType || "Holiday"}`}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: "0.75rem",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              p: 0.5,
              borderRadius: 1,
              backgroundColor: "rgba(255, 184, 28, 0.9)",
              color: "#000",
            }}
          >
            <CalendarTodayIcon fontSize="small" />
            <Typography variant="caption" noWrap>
              {userName} - {holidayType}
            </Typography>
          </Box>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        title={
          isAbsent
            ? `${userName} - Absent (${absentType || "Not specified"})`
            : `${userName} - ${time || "No time recorded"}`
        }
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: "0.75rem",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            p: 0.5,
            borderRadius: 1,
            backgroundColor: isAbsent
              ? "rgba(237, 41, 58, 0.9)"
              : "rgba(120, 182, 96, 0.9)",
            color: "#fff",
          }}
        >
          {isAbsent ? (
            <EventBusyIcon fontSize="small" />
          ) : (
            <AccessTimeIcon fontSize="small" />
          )}
          <Typography variant="caption" noWrap>
            {userName} {!isAbsent && time && `- ${time}`}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  // For stats panel
  const StatCard = ({ title, value, icon, color }) => (
    <Card
      elevation={2}
      sx={{ p: 2, height: "100%", backgroundColor: `${color}15` }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold" color={color}>
            {value}
          </Typography>
        </Box>
        <Avatar sx={{ bgcolor: color }}>{icon}</Avatar>
      </Box>
    </Card>
  );

  // Employee stats table
  const EmployeeStatsTable = () => (
    <Box mt={2}>
      <Typography variant="h6" gutterBottom>
        Employee Attendance Statistics
      </Typography>
      <Box
        sx={{
          maxHeight: 300,
          overflowY: "auto",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          "&::-webkit-scrollbar": {
            width: "0.4em",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: theme.palette.background.paper,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.mode === "dark" ? "#555" : "#ccc",
            borderRadius: 6,
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 1fr",
            bgcolor: theme.palette.background.paper,
            position: "sticky",
            top: 0,
            p: 1,
            fontWeight: "bold",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2">Employee</Typography>
          <Typography variant="subtitle2">Present</Typography>
          <Typography variant="subtitle2">Absent</Typography>
          <Typography variant="subtitle2">Total Hours</Typography>
        </Box>
        {stats.employeeStats.length > 0 ? (
          stats.employeeStats.map((stat, index) => (
            <Box
              key={index}
              sx={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1fr 1fr",
                p: 1,
                "&:nth-of-type(odd)": {
                  bgcolor: theme.palette.action.hover,
                },
              }}
            >
              <Typography variant="body2" noWrap>
                {stat.name}
              </Typography>
              <Typography variant="body2" color="success.main">
                {stat.attendanceDays}
              </Typography>
              <Typography variant="body2" color="error.main">
                {stat.absenceDays}
              </Typography>
              <Typography variant="body2">{stat.totalHours}h</Typography>
            </Box>
          ))
        ) : (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No data available
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
      <Box sx={{ p: 2 }}>
        {/* Page header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h4" fontWeight="bold" color="primary">
            Attendance Calendar
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setFormData({
                user: "",
                Date: format(new Date(), "yyyy-MM-dd"),
                StartTime: "",
                endTime: "",
                Abwesend: false,
                AbwesendType: "",
              });
              setCurrentRecord(null);
              setIsModalOpen(true);
            }}
          >
            Add Attendance
          </Button>
        </Box>

        {/* Alerts */}
        {alert.show && (
          <Alert
            severity={alert.type}
            sx={{ mb: 2 }}
            onClose={() => setAlert({ ...alert, show: false })}
          >
            {alert.message}
          </Alert>
        )}

        {/* Stats cards */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <StatCard
              title="Total Attendances"
              value={stats.totalAttendance}
              icon={<PersonIcon />}
              color={theme.palette.success.main}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              title="Total Absences"
              value={stats.totalAbsences}
              icon={<EventBusyIcon />}
              color={theme.palette.error.main}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              title="Total Hours"
              value={`${stats.totalHours}h`}
              icon={<AccessTimeIcon />}
              color={theme.palette.info.main}
            />
          </Grid>
        </Grid>

        {/* Filters */}
        <Box
          mb={3}
          p={2}
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <Box display="flex" alignItems="center" mb={1}>
            <FilterListIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Employee</InputLabel>
                <Select
                  value={filterOptions.employee}
                  onChange={(e) =>
                    handleFilterChange("employee", e.target.value)
                  }
                  label="Employee"
                >
                  <MenuItem value="">All Employees</MenuItem>
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.name}>
                      {employee.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <DatePicker
                label="From Date"
                value={filterOptions.dateRange.start}
                onChange={(newValue) => {
                  handleFilterChange("dateRange", {
                    ...filterOptions.dateRange,
                    start: newValue ? startOfDay(newValue) : null,
                  });
                }}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DatePicker
                label="To Date"
                value={filterOptions.dateRange.end}
                onChange={(newValue) => {
                  handleFilterChange("dateRange", {
                    ...filterOptions.dateRange,
                    end: newValue ? endOfDay(newValue) : null,
                  });
                }}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={6} sm={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filterOptions.showAbsent}
                    onChange={(e) =>
                      handleFilterChange("showAbsent", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label="Show Absent"
                labelPlacement="top"
              />
            </Grid>
            <Grid item xs={6} sm={1}>
              <Tooltip title="Clear Filters">
                <IconButton onClick={handleClearFilters} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>

        {/* Legend */}
        <Box
          mb={2}
          p={2}
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold" mb={1}>
            Legend:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: 1,
                  backgroundColor: "rgba(120, 182, 96, 0.9)",
                }}
              />
              <Typography variant="body2">Present</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: 1,
                  backgroundColor: "rgba(237, 41, 58, 0.9)",
                }}
              />
              <Typography variant="body2">Absent</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: 1,
                  backgroundColor: "rgba(255, 184, 28, 0.9)",
                }}
              />
              <Typography variant="body2">Holiday</Typography>
            </Box>
          </Box>
        </Box>

        {/* Calendar */}
        <Card elevation={3}>
          <Box
            p={1}
            sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
          >
            <Tabs
              value={calendarView}
              onChange={(_, newValue) => setCalendarView(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Month" value="dayGridMonth" />
              <Tab label="Week" value="timeGridWeek" />
              <Tab label="Day" value="timeGridDay" />
              <Tab label="List" value="listMonth" />
              <Tab label="Statistics" value="stats" />
            </Tabs>
          </Box>

          <Box sx={{ position: "relative", minHeight: "70vh" }}>
            {isLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  zIndex: 10,
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {calendarView === "stats" ? (
              <Box p={3}>
                <EmployeeStatsTable />
              </Box>
            ) : (
              <Box sx={{ height: "70vh" }}>
                <FullCalendar
                  plugins={[
                    dayGridPlugin,
                    timeGridPlugin,
                    interactionPlugin,
                    listPlugin,
                  ]}
                  initialView={calendarView}
                  headerToolbar={{
                    left: "prev,next today",
                    center: "title",
                    right: "",
                  }}
                  events={filteredEvents}
                  dateClick={handleDateClick}
                  eventClick={handleEventClick}
                  datesSet={handleDatesSet}
                  eventContent={renderEventContent}
                  eventTimeFormat={{
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  }}
                  firstDay={1} // Monday as first day
                  locale="de" // German locale
                  height="100%"
                />
              </Box>
            )}
          </Box>
        </Card>

        {/* Attendance Modal */}
        <Dialog
          open={isModalOpen}
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {currentRecord ? "Update Attendance" : "Add Attendance"}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Employee</InputLabel>
                  <Select
                    value={formData.user}
                    onChange={(e) =>
                      setFormData({ ...formData, user: e.target.value })
                    }
                    label="Employee"
                  >
                    <MenuItem value="">Select Employee</MenuItem>
                    {employees.map((employee) => (
                      <MenuItem key={employee.id} value={employee.name}>
                        {employee.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Date"
                  value={formData.Date ? new Date(formData.Date) : null}
                  onChange={(newValue) => {
                    if (newValue && isValid(newValue)) {
                      setFormData({
                        ...formData,
                        Date: format(newValue, "yyyy-MM-dd"),
                      });
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth margin="normal" />
                  )}
                  slotProps={{
                    textField: { fullWidth: true, margin: "normal" },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.Abwesend}
                      onChange={(e) =>
                        setFormData({ ...formData, Abwesend: e.target.checked })
                      }
                      color="primary"
                    />
                  }
                  label="Absent"
                  sx={{ mt: 3 }}
                />
              </Grid>

              {formData.Abwesend ? (
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Absence Type</InputLabel>
                    <Select
                      value={formData.AbwesendType}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          AbwesendType: e.target.value,
                        })
                      }
                      label="Absence Type"
                    >
                      <MenuItem value="">Not Specified</MenuItem>
                      <MenuItem value="urlaub">Urlaub</MenuItem>
                      <MenuItem value="krank">Krank</MenuItem>
                      <MenuItem value="unpaid-urlaub">Unpaid Urlaub</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} sm={6}>
                    <TimePicker
                      label="Start Time"
                      value={
                        formData.StartTime
                          ? parseTime(formData.StartTime)
                          : null
                      }
                      onChange={(newValue) => {
                        if (newValue && isValid(newValue)) {
                          setFormData({
                            ...formData,
                            StartTime: format(newValue, "HH:mm"),
                          });
                        }
                      }}
                      slotProps={{
                        textField: { fullWidth: true, margin: "normal" },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TimePicker
                      label="End Time"
                      value={
                        formData.endTime ? parseTime(formData.endTime) : null
                      }
                      onChange={(newValue) => {
                        if (newValue && isValid(newValue)) {
                          setFormData({
                            ...formData,
                            endTime: format(newValue, "HH:mm"),
                          });
                        }
                      }}
                      slotProps={{
                        textField: { fullWidth: true, margin: "normal" },
                      }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            {currentRecord && (
              <Button
                startIcon={<DeleteIcon />}
                color="error"
                onClick={handleDeleteAttendance}
                disabled={isLoading}
              >
                Delete
              </Button>
            )}
            <Button onClick={handleCloseModal} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveAttendance}
              variant="contained"
              color="primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : currentRecord ? (
                "Update"
              ) : (
                "Add"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

// Helper function to parse time strings
const parseTime = (timeString) => {
  if (!timeString) return null;

  const parts = timeString.split(":");
  if (parts.length < 2) return null;

  const date = new Date();
  date.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
  return date;
};

export default AttendanceCalendar;
